/**
 * Onboarding Screen
 * AI-driven conversational onboarding with 4-question diagnostic flow
 */

import React, { useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Text,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useOnboardingStore } from '../store/onboardingStore';
import OnboardingMessage from '../components/onboarding/OnboardingMessage';
import OnboardingInput from '../components/onboarding/OnboardingInput';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import logger from '../utils/logger';
import type { OnboardingStepData } from '../api/types';
import { analytics, AnalyticsEvent } from '../utils/analytics';

interface OnboardingScreenProps {
  onComplete?: () => void;
}

export default function OnboardingScreen({ onComplete }: OnboardingScreenProps) {
  const {
    currentStep,
    diagnosticData,
    isLoading,
    error,
    fetchStatus,
    submitStep,
    submitDiagnosticAction,
    clearError,
  } = useOnboardingStore();
  const scrollViewRef = useRef<ScrollView>(null);

  // Scroll to top when step changes
  useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, [currentStep?.step]);

  // Load onboarding status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  // Handle completion
  useEffect(() => {
    if (currentStep?.completed) {
      analytics.trackEvent(AnalyticsEvent.PROFILE_COMPLETED, {
        onboarding_step: currentStep.step,
      });

      if (onComplete) {
        onComplete();
      }
    }
  }, [currentStep?.completed, onComplete]);

  const handleSubmit = async (data: OnboardingStepData) => {
    if (!currentStep) return;

    try {
      await submitStep(currentStep.step, data);
    } catch (err) {
      logger.error('[OnboardingScreen] Submit error:', err);
    }
  };

  const handleDiagnosticAction = async (action: string) => {
    try {
      await submitDiagnosticAction(action);
      // Navigation is handled by AppNavigator (switches to Main when step = complete)
      // and MainContent (consumes pendingNavigationAction)
    } catch (err) {
      logger.error('[OnboardingScreen] Diagnostic action error:', err);
    }
  };

  // Loading state while fetching initial status
  if (isLoading && !currentStep) {
    return (
      <SafeAreaView style={styles.container}>
        <Loading fullScreen message="Chargement..." />
      </SafeAreaView>
    );
  }

  // Error state
  if (!currentStep) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            Erreur lors du chargement de l'onboarding
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Diagnostic step — full-screen card, no header/message wrapper
  if (currentStep.step === 'diagnostic') {
    return (
      <SafeAreaView style={styles.container}>
        {error && (
          <ErrorMessage error={error} onDismiss={clearError} style={styles.error} />
        )}
        <OnboardingInput
          step={currentStep.step}
          inputType="diagnostic"
          diagnosticData={diagnosticData}
          onSubmit={() => {}}
          onDiagnosticAction={handleDiagnosticAction}
          isLoading={isLoading}
        />
      </SafeAreaView>
    );
  }

  // Completion — AppNavigator will auto-switch to Main
  if (currentStep.completed) {
    return (
      <SafeAreaView style={styles.container}>
        <Loading fullScreen message="Bienvenue..." />
      </SafeAreaView>
    );
  }

  // Step indicator
  const currentStepNum = currentStep.message.current_step;
  const totalSteps = currentStep.message.total_steps;
  const showStepIndicator = currentStepNum != null && currentStepNum > 0 && !!totalSteps;

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Wealfi</Text>
        </View>

        {error && (
          <ErrorMessage error={error} onDismiss={clearError} style={styles.error} />
        )}

        <ScrollView
          ref={scrollViewRef}
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          keyboardShouldPersistTaps="handled"
        >
          {/* AI Message */}
          <OnboardingMessage message={currentStep.message.message} />

          {/* Step indicator — only for Q1-Q4 */}
          {showStepIndicator && (
            <View style={styles.stepIndicator}>
              <Text style={styles.stepText}>
                Question {currentStepNum}/{totalSteps}
              </Text>
            </View>
          )}
        </ScrollView>

        {/* Input Section - Fixed at bottom */}
        <View style={styles.inputSection}>
          <OnboardingInput
            step={currentStep.step}
            inputType={currentStep.message.input_type}
            options={currentStep.message.options}
            buttonLabel={currentStep.message.button_label}
            onSubmit={handleSubmit}
            isLoading={isLoading}
          />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  keyboardAvoid: {
    flex: 1,
  },
  header: {
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  headerTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },
  error: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: spacing.lg,
  },
  stepIndicator: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  stepText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
  inputSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    fontSize: typography.fontSize.md,
    color: colors.error,
    textAlign: 'center',
  },
});
